import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { SectionHeading } from "@/components/ui/section-heading";
import { useConfirmationDialog } from "@/lib/confirmation-dialog";
import { apiService } from "@/lib/api-service";
import { useAuth } from "@/lib/auth-context";
import { UserRole } from "@employee-management/common";
import { RefreshCcw } from "lucide-react";

export function MaintenanceTab() {
  const { hasRole } = useAuth();
  const { showConfirmation } = useConfirmationDialog();

  const initializeMutation = useMutation({
    mutationFn: async () =>
      apiService.post<void>("/app-settings/initialize-project-metadata"),
    onSuccess: () => {
      toast.success(
        "Project metadata initialized and updated for all projects"
      );
    },
    onError: () => {
      toast.error("Failed to initialize project metadata");
    },
  });

  const handleInitialize = async () => {
    await showConfirmation({
      title: "Initialize Project Metadata",
      description:
        "This will create missing metadata and recalculate statistics for all projects. Continue?",
      type: "warning",
      confirmText: "Run Initialization",
      cancelText: "Cancel",
      onConfirm: async () => {
        await initializeMutation.mutateAsync();
      },
    });
  };

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Maintenance"
        subtitle="Run administrative and data maintenance operations."
        actions={
          hasRole(UserRole.SUPERADMIN) ? (
            <Button
              onClick={handleInitialize}
              disabled={initializeMutation.isPending}
              variant="outline"
            >
              <RefreshCcw className="h-4 w-4" />
              {initializeMutation.isPending
                ? "Initializing..."
                : "Initialize Project Metadata"}
            </Button>
          ) : undefined
        }
      />

      <div className="rounded-lg border bg-card p-4 text-sm text-muted-foreground">
        Use this tool to ensure all projects have metadata and to recalculate
        summary statistics.
      </div>
    </div>
  );
}
